import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MaxulusBrand from '../components/MaxulusBrand';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Checkbox,
  Stack
} from '@mui/material';
import {
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getLanguage } from '../utils/locale';
import SubscriptionExpiredModal from '../components/SubscriptionExpiredModal';
import { landingPath } from '../utils/landingPath';
import { ledger, FONT_DISPLAY } from '../theme/modernTheme';

const LoginInteractive: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const L = ledger(theme);

  const [formData, setFormData] = useState({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState<{
    open: boolean; endDate?: string; daysExpired?: number; username?: string;
  }>({ open: false });

  useEffect(() => {
    const expiredInfo = localStorage.getItem('subscription_expired');
    if (expiredInfo) {
      const info = JSON.parse(expiredInfo);
      const endDate = new Date(info.endDate);
      const now = new Date();
      const daysExpired = Math.floor((now.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
      setSubscriptionExpired({ open: true, endDate: info.endDate, daysExpired, username: info.username });
      localStorage.removeItem('subscription_expired');
    }
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    if (rememberedUsername) {
      setFormData(prev => ({ ...prev, username: rememberedUsername }));
      setRememberMe(true);
      setTimeout(() => passwordRef.current?.focus(), 100);
    } else {
      setTimeout(() => usernameRef.current?.focus(), 100);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    const renderWidget = () => {
      const ts = (window as any).turnstile;
      if (turnstileRef.current && ts && widgetIdRef.current === null) {
        widgetIdRef.current = ts.render(turnstileRef.current, {
          sitekey: '0x4AAAAAAFHx5N49Oqdcd_Lm',
          callback: (token: string) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileToken(''),
          theme: 'auto',
          size: 'flexible',
          language: getLanguage(),
        });
      }
    };
    const timer = setTimeout(renderWidget, 500);
    return () => clearTimeout(timer);
  }, []);

  const resetTurnstile = () => {
    setTurnstileToken('');
    const ts = (window as any).turnstile;
    if (ts && widgetIdRef.current !== null) ts.reset(widgetIdRef.current);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      setError(t('Kullanıcı adı ve şifre gereklidir'));
      return;
    }
    if (!turnstileToken) {
      setError(t('Lütfen robot doğrulamasını tamamlayın'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(formData.username, formData.password, undefined, turnstileToken);
      if (rememberMe) {
        localStorage.setItem('rememberedUsername', formData.username);
      } else {
        localStorage.removeItem('rememberedUsername');
      }
      navigate(landingPath(JSON.parse(localStorage.getItem('user') || '{}')));
    } catch (err: any) {
      if (err.response?.data?.error === 'SUBSCRIPTION_EXPIRED') {
        setSubscriptionExpired({
          open: true,
          endDate: err.response.data.subscriptionEndDate,
          daysExpired: err.response.data.daysExpired,
          username: formData.username
        });
      } else if (err.message === 'SUBSCRIPTION_EXPIRED') {
        setSubscriptionExpired({
          open: true,
          endDate: err.response?.data?.subscriptionEndDate || new Date().toISOString(),
          daysExpired: err.response?.data?.daysExpired || 0,
          username: formData.username
        });
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || err.message || t('Giriş başarısız'));
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
      // Reset Turnstile so the next attempt gets a fresh single-use token
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: L.canvas,
        py: 4,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto', px: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <MaxulusBrand height={36} label="CRM" dark={isDarkMode} />
        </Box>

        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 1.5,
            backgroundColor: L.surface,
            border: `1px solid ${L.rule}`,
            animation: shake ? 'shake 0.4s ease-in-out' : 'none',
            '@keyframes shake': {
              '0%, 100%': { transform: 'translateX(0)' },
              '20%': { transform: 'translateX(-6px)' },
              '40%': { transform: 'translateX(6px)' },
              '60%': { transform: 'translateX(-3px)' },
              '80%': { transform: 'translateX(3px)' },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          <Typography
            component="h1"
            sx={{ fontFamily: FONT_DISPLAY, fontSize: '1.375rem', fontWeight: 600, letterSpacing: '-0.01em', color: L.ink, mb: 0.5 }}
          >
            {t('Giriş yap')}
          </Typography>
          <Typography variant="body2" sx={{ color: L.inkSecondary, mb: 3 }}>
            {t('Hesabınıza giriş yaparak devam edin')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                name="username"
                label={t('Kullanıcı adı')}
                value={formData.username}
                onChange={handleChange}
                autoComplete="username"
                inputRef={usernameRef}
              />

              <TextField
                fullWidth
                name="password"
                label={t('Şifre')}
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                autoComplete="current-password"
                inputRef={passwordRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? t('Şifreyi gizle') : t('Şifreyi göster')}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                sx={{ mt: '4px !important' }}
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{ color: L.inkSecondary }}>{t('Beni hatırla')}</Typography>}
              />

              <Box ref={turnstileRef} sx={{ display: 'flex', justifyContent: 'center', minHeight: 65, '& iframe': { maxWidth: '100%' } }} />

              <Button
                type="submit"
                fullWidth
                size="large"
                variant="contained"
                disabled={loading || !turnstileToken}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {loading ? t('Giriş yapılıyor...') : t('Giriş yap')}
              </Button>
            </Stack>
          </form>
        </Box>

        <Typography variant="caption" component="p" sx={{ textAlign: 'center', color: L.inkTertiary, mt: 3 }}>
          {t('Maxulus, bir Dijital Klinik ürünüdür')}
        </Typography>
      </Box>

      <SubscriptionExpiredModal
        open={subscriptionExpired.open}
        onClose={() => setSubscriptionExpired({ open: false })}
        endDate={subscriptionExpired.endDate}
        daysExpired={subscriptionExpired.daysExpired}
        username={subscriptionExpired.username}
      />
    </Box>
  );
};

export default LoginInteractive;
